import { t } from "ttag";

import { AdminNavItem } from "metabase/admin/components/AdminNav";
import { UpsellGem } from "metabase/common/components/upsells/components";
import { useSetting } from "metabase/common/hooks";
import type { PaletteSubPath } from "metabase/palette/types";

export function getAiControlsNavItems() {
  return <AiControlsNavItems />;
}

// Mirrors AiControlsNavItems' own sub-page list below so every sub-page
// reachable by browsing Admin > AI's sidebar is also reachable by name from
// the global command palette (WCAG 2.4.5 Multiple Ways).
export function useAiControlsPaths(): PaletteSubPath[] {
  const areAiFeaturesEnabled = useSetting("ai-features-enabled?");

  if (!areAiFeaturesEnabled) {
    return [];
  }

  const aiLabel = t`AI`;
  const usageControlsLabel = t`Usage controls`;
  const systemPromptsLabel = t`System prompts`;

  return [
    {
      key: "ai-usage-controls-access",
      name: `${aiLabel} - ${usageControlsLabel} - ${t`Access`}`,
      path: "/admin/metabot/usage-controls/ai-feature-access",
    },
    {
      key: "ai-usage-controls-limits",
      name: `${aiLabel} - ${usageControlsLabel} - ${t`Limits`}`,
      path: "/admin/metabot/usage-controls/ai-usage-limits",
    },
    {
      key: "ai-customization",
      name: `${aiLabel} - ${t`Customization`}`,
      path: "/admin/metabot/customization",
    },
    {
      key: "ai-system-prompts-chat",
      name: `${aiLabel} - ${systemPromptsLabel} - ${t`AI chat`}`,
      path: "/admin/metabot/system-prompts/metabot-chat",
    },
    {
      key: "ai-system-prompts-nlq",
      name: `${aiLabel} - ${systemPromptsLabel} - ${t`Natural language queries`}`,
      path: "/admin/metabot/system-prompts/natural-language-queries",
    },
    {
      key: "ai-system-prompts-sql",
      name: `${aiLabel} - ${systemPromptsLabel} - ${t`SQL generation`}`,
      path: "/admin/metabot/system-prompts/sql-generation",
    },
  ];
}

export function getAiControlsUpsellNavItems() {
  return <AiControlsUpsellNavItems />;
}

function AiControlsNavItems() {
  const isConfigured = useSetting("llm-metabot-configured?");
  const areAiFeaturesEnabled = useSetting("ai-features-enabled?");

  if (!areAiFeaturesEnabled) {
    return null;
  }

  return (
    <>
      <AdminNavItem
        icon="lock"
        label={t`Usage controls`}
        folderPattern="usage-controls"
        disabled={!isConfigured}
      >
        <AdminNavItem
          label={t`Access`}
          path="/admin/metabot/usage-controls/ai-feature-access"
          disabled={!isConfigured}
        />
        <AdminNavItem
          label={t`Limits`}
          path="/admin/metabot/usage-controls/ai-usage-limits"
        />
      </AdminNavItem>
      <AdminNavItem
        icon="palette"
        label={t`Customization`}
        path="/admin/metabot/customization"
        disabled={!isConfigured}
      />
      <AdminNavItem
        icon="document"
        label={t`System prompts`}
        folderPattern="system-prompts"
        disabled={!isConfigured}
      >
        <AdminNavItem
          label={t`AI chat`}
          path="/admin/metabot/system-prompts/metabot-chat"
          disabled={!isConfigured}
        />
        <AdminNavItem
          label={t`Natural language queries`}
          path="/admin/metabot/system-prompts/natural-language-queries"
          disabled={!isConfigured}
        />
        <AdminNavItem
          label={t`SQL generation`}
          path="/admin/metabot/system-prompts/sql-generation"
          disabled={!isConfigured}
        />
      </AdminNavItem>
    </>
  );
}

function AiControlsUpsellNavItems() {
  return (
    <>
      <AdminNavItem
        icon="lock"
        label={t`Usage controls`}
        path="/admin/metabot/usage-controls/ai-feature-access"
        rightSection={<UpsellGem.New size={14} />}
      />
      <AdminNavItem
        icon="palette"
        label={t`Customization`}
        path="/admin/metabot/customization"
        rightSection={<UpsellGem.New size={14} />}
      />
      <AdminNavItem
        icon="document"
        label={t`System prompts`}
        path="/admin/metabot/system-prompts/metabot-chat"
        rightSection={<UpsellGem.New size={14} />}
      />
    </>
  );
}
